import type { ContentTypeId, ContentTypes } from 'lib/contentful/fetchResource';
import { removeUndefinedTopLevelKeys } from 'lib/util';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';

interface UseContentResourceArgs<T extends ContentTypes, U> {
  slug?: string;
  limit?: number;
  type: ContentTypeId;
  query?: Record<string, string | number>;
  mapper?: (content: T) => U;
}

export default function useContentResource<T extends ContentTypes, U>({
  slug,
  limit = 1,
  type,
  query = {},
  mapper,
}: UseContentResourceArgs<T, U>): {
  data: U[];
  error: Error | null;
  loading: boolean;
} {
  const [data, setData] = useState<U[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const hash = `${slug},${limit},${type},${JSON.stringify(query)}`;

  const fetchResource = useCallback(async () => {
    setData([]);
    setLoading(true);
    setError(null);

    try {
      const queryString = qs.stringify(
        removeUndefinedTopLevelKeys({
          type,
          limit,
          slug,
          ...query,
        })
      );

      const resource = await fetch(
        `/services/content/fetch-resource?${queryString}`
      );

      const unrefinedContent = (await resource.json()) as T[];

      const content = (
        mapper ? unrefinedContent.map(mapper) : unrefinedContent
      ) as (U | null)[];

      setData(content.filter((c): c is U => c !== null));

      setLoading(false);
    } catch (e) {
      setLoading(false);

      setError(e as Error);
    }
  }, [hash]);

  useEffect(() => {
    fetchResource();
  }, [hash]);

  return { data, loading, error };
}
